import { useEffect, useRef, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { levelProgress, PROGRESSION, type GameId, type ProgressEvent } from '@tg/shared';
import { useSession } from '@/stores/session';
import { toast } from '@/stores/toasts';
import { useSocketEvent } from '@/socket/socket';
import { fx, haptic, sound, useCelebration, wait } from '@/fx';
import { cx } from '@/ui';
import { clearInviteCookie } from './util';
import s from './progression.module.css';

/**
 * Avatar with the level ring (design Motion "Level ring": thin ring fills after every round) and the
 * win-streak badge ×2…×5 (gold at ×5). Used by the shell's account button.
 */
export function LevelRing({ size = 34, children }: { size?: number; children: ReactNode }) {
  const intl = useIntl();
  const me = useSession((st) => st.me);
  const p = levelProgress(me?.xp ?? 0);
  const level = me?.level ?? p.level;
  const streak = Math.min(me?.streak ?? 0, PROGRESSION.streakMax);
  const box = size + 8;
  const r = box / 2 - 1.5;
  const c = 2 * Math.PI * r;
  const label = intl.formatMessage({ id: 'social.level.label' }, { level, into: p.into, need: p.need });
  return (
    <span className={s.wrap} style={{ width: box, height: box }} data-level-ring="" title={label}>
      <svg className={s.ring} width={box} height={box} aria-hidden="true">
        <circle cx={box / 2} cy={box / 2} r={r} className={s.track} />
        <circle cx={box / 2} cy={box / 2} r={r} className={s.fill} strokeDasharray={c} strokeDashoffset={c * (1 - p.ratio)} />
      </svg>
      {children}
      <span className={s.level} aria-hidden="true">
        {level}
      </span>
      {streak >= 2 && (
        <span
          key={streak}
          className={cx(s.streak, streak >= PROGRESSION.streakMax && s.streakMax)}
          title={intl.formatMessage({ id: 'social.streak.label' }, { count: streak })}
        >
          ×{streak}
        </span>
      )}
      <span className="visually-hidden">{label}</span>
    </span>
  );
}

/**
 * Global listener (mounted once in App): progress (level up → fireworks at the ring, streak ×5 → confetti),
 * tournament results (top 3 → confetti), friend invitations. Every celebration is skippable with a tap.
 */
export function SocialListener() {
  const intl = useIntl();
  const cel = useCelebration();
  const status = useSession((st) => st.status);
  const prevStreak = useRef(0);

  useEffect(() => {
    if (status === 'user') clearInviteCookie();
    prevStreak.current = useSession.getState().me?.streak ?? 0;
  }, [status]);

  useSocketEvent<[ProgressEvent]>('progress:update', (e) => {
    const st = useSession.getState();
    if (!st.me) return;
    const before = prevStreak.current;
    prevStreak.current = e.streak;
    useSession.setState({ me: { ...st.me, level: e.level, xp: e.xp, streak: e.streak } });
    if (e.levelUp) {
      // the reward is already credited by the server (ledger `level_reward`); the balance rolls quietly
      // the level fanfare below is the sound of this toast
      if (e.reward > 0) toast.success('social.level.upReward', { level: e.level, amount: e.reward }, { sound: false });
      else toast.success('social.level.up', { level: e.level }, { sound: false });
      void cel.run(async (signal) => {
        await wait(400, signal);
        const ring = document.querySelector('[data-level-ring]');
        if (ring) fx.pop(ring);
        sound.play('common/level', { signal });
        haptic('double');
        fx.fireworks(4, ring);
        await wait(1800, signal);
      });
    } else if (e.streak >= PROGRESSION.streakMax && before < PROGRESSION.streakMax) {
      void cel.run(async (signal) => {
        fx.confetti();
        haptic('light');
        await wait(2500, signal);
      });
    }
  });

  useSocketEvent<[{ id: string; title: string; place: number; prize: number }]>('tournament:result', (e) => {
    toast.success('social.tour.resultToast', { title: e.title, place: e.place, amount: e.prize });
    if (e.place <= 3) {
      void cel.run(async (signal) => {
        fx.confetti();
        haptic('medium');
        await wait(3500, signal);
      });
    }
  });

  useSocketEvent<[{ publicId: number; nickname: string; game: GameId | null }]>('friends:invite', (e) => {
    const game = e.game ? intl.formatMessage({ id: `common.game.${e.game}` }) : '';
    toast.notice(e.game ? 'social.friends.invitedYou' : 'social.friends.invitedYouLobby', undefined, { name: e.nickname, game });
  });

  return null;
}
